/**
 * PageCanvas — renders one page with bbox overlays.
 *
 * Phase-1 Stage 1: bboxes are clickable. Selection is owned by the parent so
 * keyboard shortcuts (U/T/R/Esc) can act on the same target.
 *
 * Phase-1 Stage 3: when drawMode is on, blank-canvas mousedown starts a
 * preview rectangle that follows the cursor; on mouseup, onDrawComplete is
 * fired with the normalized bbox so the parent can POST a manual_box.
 */
import { useEffect, useMemo, useState } from "react";
import { Stage, Layer, Image as KImage, Rect, Circle, Text, Tag, Label } from "react-konva";
import type Konva from "konva";
import { colors, objectStatusColor } from "@/theme/colors";
import { skuColor, withAlpha } from "@/theme/skuColor";
import type {
  AnnotationObject,
  BBox,
  ObjectStatus,
  Page as ApiPage,
} from "@/api/cycles";
import { preferredAssetUrl } from "@/api/cycles";

/** Per-object visual overrides — used by Stage 2 to highlight the active
 * overlap pair (A in intelligence blue, B in overlap-b cyan) without
 * mutating the underlying annotations. */
export type CanvasOverride = {
  stroke?: string;
  thickness?: number;
  dashed?: boolean;
  /** Short corner badge (e.g. "A" / "B") painted inside the box. Used by
   *  Sobreposições to make it unmistakable which mark is A and which is B
   *  when only the active pair is shown. */
  badge?: string;
  /** Which corner to anchor the badge in. Defaults to top-left. Sobreposições
   *  anchors A top-left and B top-right so the two labels don't collide when
   *  the boxes overlap heavily (Feedback V2 rev02 r2-3). */
  badgeCorner?: "tl" | "tr";
};

type Props = {
  cycleId: string;
  page: ApiPage;
  objects: AnnotationObject[];
  /** Width budget in CSS pixels for the stage. Height derives from page aspect. */
  stageWidth: number;
  selectedObjectId: string | null;
  onSelect: (objectId: string | null) => void;
  /** Optional per-object visual overrides keyed by object id. */
  overrides?: Record<string, CanvasOverride>;
  /** When true, blank-canvas mousedown starts a draw rectangle.
   *  Source=manual boxes also become draggable to move. */
  drawMode?: boolean;
  /** When false, draw initiation is suppressed even though drawMode is on
   *  (e.g. operator hasn't picked a SKU yet). Move/resize on existing
   *  tags is unaffected. */
  canDraw?: boolean;
  /** Fired after a draw gesture ends with a non-trivial rectangle.
   *  bbox is normalized [0,1] against the page. */
  onDrawComplete?: (bbox: BBox) => void;
  /** Fired after a manual_box drag ends. bbox normalized to [0,1]. */
  onObjectMoved?: (objectId: string, bbox: BBox) => void;
  /** Stage 3 explicit move mode. When true and a tag is selected, that
   *  tag's full interior is filled and draggable; other tags are unchanged. */
  moveMode?: boolean;
  /** When true, every rendered rect gets a transparent stroke-tinted fill
   *  (no impact on hit area). Used in Triagem so the bbox is easy to spot
   *  on top of complex page art. */
  tintAll?: boolean;
};

const MIN_DRAW_PX = 4; // ignore micro-clicks that are obviously not a real box

export function PageCanvas({
  cycleId,
  page,
  objects,
  stageWidth,
  selectedObjectId,
  onSelect,
  overrides,
  drawMode = false,
  canDraw = true,
  moveMode = false,
  tintAll = false,
  onDrawComplete,
  onObjectMoved,
}: Props) {
  // The loaded image is tracked together with the URL it was loaded for.
  // On a page change page.renderKey (and the derived URL) change immediately,
  // but the previously-loaded image would otherwise linger in state until the
  // new page's onload fires. Deriving the rendered `img` from a URL match
  // means we show the "Carregando…" placeholder during that gap instead of
  // painting the NEW page's bboxes on top of the OLD page's image — the
  // latter made operators flag a correct mark as trash because they were
  // looking at the wrong page (Feedback V2 #4).
  const [loaded, setLoaded] = useState<{ url: string; img: HTMLImageElement } | null>(
    null,
  );
  const [imgError, setImgError] = useState<string | null>(null);
  const currentUrl = preferredAssetUrl(cycleId, page.renderKey, page.renderUrl);

  useEffect(() => {
    if (!currentUrl) return;
    let cancelled = false;
    setImgError(null);
    const node = new window.Image();
    node.crossOrigin = "anonymous";
    node.onload = () => {
      if (!cancelled) setLoaded({ url: currentUrl, img: node });
    };
    node.onerror = () => {
      if (!cancelled) setImgError(`Falha ao carregar ${page.renderKey}`);
    };
    node.src = currentUrl;
    // A cached image can already be decoded; resolve synchronously so flipping
    // back to a visited page doesn't flash the placeholder.
    if (node.complete && node.naturalWidth > 0) {
      setLoaded({ url: currentUrl, img: node });
    }
    return () => {
      cancelled = true;
    };
  }, [currentUrl, page.renderKey]);

  // Only treat the image as ready when it matches the page currently shown.
  const img = loaded && loaded.url === currentUrl ? loaded.img : null;

  const aspect = page.heightPt / page.widthPt;
  const stageHeight = Math.round(stageWidth * aspect);

  // Render order: biggest area first, smallest last. Konva paints later
  // shapes on top, AND its hit-test walks the same stack top-down — so
  // when a click falls inside several overlapping bboxes, the smallest
  // one wins. That matches operator intent: when a tiny tag sits inside
  // a bigger one (e.g. a logo inside a product photo), clicking the
  // overlap selects the small one.
  const orderedObjects = useMemo(
    () =>
      [...objects].sort((a, b) => {
        const areaA = a.bbox.w * a.bbox.h;
        const areaB = b.bbox.w * b.bbox.h;
        return areaB - areaA;
      }),
    [objects],
  );

  // Draw-mode local state: the in-flight preview rectangle, in stage px.
  const [drawStart, setDrawStart] = useState<{ x: number; y: number } | null>(null);
  const [drawEnd, setDrawEnd] = useState<{ x: number; y: number } | null>(null);

  const handleStageMouseDown = (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
    const stage = e.target.getStage();
    if (!stage) return;
    const pos = stage.getPointerPosition();
    if (!pos) return;

    const targetIsBlank =
      e.target === stage || e.target.attrs.name === "page-image";

    if (drawMode) {
      // Bbox rects intercept their own mousedown (select / drag / resize)
      // via cancelBubble. Only start a draw gesture when the click landed
      // on the empty stage or the page image itself — never on top of an
      // existing rect, otherwise we'd both start a drag AND a draw.
      if (!targetIsBlank) return;
      // Draw initiation is gated by canDraw — operators must pick a SKU
      // first so the new tag is born already labeled. Click still
      // deselects below.
      if (!canDraw) {
        onSelect(null);
        return;
      }
      setDrawStart(pos);
      setDrawEnd(pos);
      return;
    }

    if (targetIsBlank) {
      onSelect(null);
    }
  };

  const handleStageMouseMove = (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
    if (!drawMode || !drawStart) return;
    const pos = e.target.getStage()?.getPointerPosition();
    if (!pos) return;
    setDrawEnd(pos);
  };

  const handleStageMouseUp = () => {
    if (!drawMode || !drawStart || !drawEnd) {
      setDrawStart(null);
      setDrawEnd(null);
      return;
    }
    const x0 = Math.min(drawStart.x, drawEnd.x);
    const y0 = Math.min(drawStart.y, drawEnd.y);
    const x1 = Math.max(drawStart.x, drawEnd.x);
    const y1 = Math.max(drawStart.y, drawEnd.y);
    setDrawStart(null);
    setDrawEnd(null);
    if (x1 - x0 < MIN_DRAW_PX || y1 - y0 < MIN_DRAW_PX) {
      // Treated as a click on empty canvas: deselect any active tag.
      onSelect(null);
      return;
    }
    if (onDrawComplete) {
      onDrawComplete({
        x: clamp01(x0 / stageWidth),
        y: clamp01(y0 / stageHeight),
        w: clamp01((x1 - x0) / stageWidth),
        h: clamp01((y1 - y0) / stageHeight),
      });
    }
  };

  if (imgError) {
    return (
      <div
        style={{
          ...placeholderStyle,
          width: stageWidth,
          height: stageHeight,
          color: colors.danger,
        }}
      >
        {imgError}
      </div>
    );
  }

  if (!img) {
    return (
      <div
        style={{
          ...placeholderStyle,
          width: stageWidth,
          height: stageHeight,
          color: colors.textMuted,
        }}
      >
        Carregando página…
      </div>
    );
  }

  const drawRect =
    drawMode && drawStart && drawEnd
      ? {
          x: Math.min(drawStart.x, drawEnd.x),
          y: Math.min(drawStart.y, drawEnd.y),
          w: Math.abs(drawEnd.x - drawStart.x),
          h: Math.abs(drawEnd.y - drawStart.y),
        }
      : null;

  return (
    <Stage
      width={stageWidth}
      height={stageHeight}
      onMouseDown={handleStageMouseDown}
      onTouchStart={handleStageMouseDown}
      onMouseMove={handleStageMouseMove}
      onTouchMove={handleStageMouseMove}
      onMouseUp={handleStageMouseUp}
      onTouchEnd={handleStageMouseUp}
      style={{
        background: colors.canvas,
        border: `1px solid ${colors.line}`,
        borderRadius: 4,
        cursor: drawMode && canDraw ? "crosshair" : "default",
      }}
    >
      <Layer>
        <KImage
          image={img}
          width={stageWidth}
          height={stageHeight}
          name="page-image"
          // In draw mode, the image must NOT consume mouse events so the
          // stage handler runs.
          listening={!drawMode}
        />
      </Layer>
      <Layer>
        {orderedObjects.map((o) => {
          // In Stage 3, every box (manual or extracted) can be moved /
          // resized so operators can fix slightly-off crops on pipeline
          // boxes without redrawing them. Delete remains manual-only —
          // extracted boxes get retired via trash status in Stage 1.
          const draggable = drawMode;
          const interactive = true;
          const isMoveTarget = moveMode && o.id === selectedObjectId;
          return (
            <BoundingBox
              key={o.id}
              object={o}
              stageWidth={stageWidth}
              stageHeight={stageHeight}
              selected={o.id === selectedObjectId}
              override={overrides?.[o.id]}
              interactive={interactive}
              draggable={draggable}
              drawMode={drawMode}
              moveTarget={isMoveTarget}
              tinted={tintAll || isMoveTarget}
              onClick={() => onSelect(o.id === selectedObjectId ? null : o.id)}
              onDragEnd={(bbox) => onObjectMoved?.(o.id, bbox)}
            />
          );
        })}
        {drawRect && (
          <Rect
            x={drawRect.x}
            y={drawRect.y}
            width={drawRect.w}
            height={drawRect.h}
            stroke={colors.manual}
            strokeWidth={2}
            dash={[4, 4]}
            fill="rgba(255,127,14,0.10)"
            listening={false}
          />
        )}
      </Layer>
    </Stage>
  );
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

const MIN_RESIZE_PX = 4;

function BoundingBox({
  object,
  stageWidth,
  stageHeight,
  selected,
  override,
  interactive,
  draggable,
  drawMode,
  moveTarget,
  tinted,
  onClick,
  onDragEnd,
}: {
  object: AnnotationObject;
  stageWidth: number;
  stageHeight: number;
  selected: boolean;
  override?: CanvasOverride;
  interactive: boolean;
  draggable: boolean;
  drawMode: boolean;
  /** This is the selected tag AND move-mode is active — give it a
   *  transparent fill and make the whole interior draggable. */
  moveTarget: boolean;
  /** Visual-only: paint a transparent stroke-tinted fill so the bbox is
   *  easy to spot. Does NOT change hit area. */
  tinted: boolean;
  onClick: () => void;
  onDragEnd: (bbox: BBox) => void;
}) {
  // When a tag has been labeled, prefer its SKU color over the status color
  // — it makes "find me the body-lotion tags" instant. Status color still
  // wins as a fallback for unlabeled boxes (suggested/confirmed/manual/etc).
  const labeledColor = object.class ? skuColor(object.class) : null;
  const baseStroke =
    labeledColor?.stroke ??
    objectStatusColor[object.status as ObjectStatus] ??
    colors.intelligence;
  const stroke = override?.stroke ?? baseStroke;
  const thickness = override?.thickness ?? (selected ? 4 : 2);
  const baseDashed = object.status === "suggested";
  const dashed = override?.dashed ?? baseDashed;

  // Local "preview" bbox in stage px — only set during a drag so the rect
  // and its corner anchors track the cursor live without round-tripping
  // through state / Firestore. Reset by the useEffect below when the
  // parent's optimistic queryData update lands (i.e. object.bbox changes).
  //
  // Why not reset synchronously in onDragEnd? TanStack's onMutate is async
  // (it `await`s cancelQueries before setQueryData). Between dragEnd and
  // the optimistic update, baseBox still points at the OLD bbox; clearing
  // the preview in that gap makes the rect snap back to the start position
  // and Konva, seeing the prop x flip, resets node.x to the OLD value.
  type PxBox = { x: number; y: number; w: number; h: number };
  const [resizePreview, setResizePreview] = useState<PxBox | null>(null);
  // Move drag tracks position only (size doesn't change). Kept in sync with
  // Konva's internal node position via onDragMove so the React-rendered x/y
  // matches what Konva is showing — no jitter on re-render.
  const [movePreview, setMovePreview] = useState<{ x: number; y: number } | null>(
    null,
  );

  // Once the parent's bbox catches up with what we dragged to (optimistic
  // update or server response), clear the previews so subsequent renders go
  // back to using object.bbox directly. If the mutation errors and the
  // bbox reverts to its pre-drag value, this also clears the preview and
  // the rect snaps to the original — the desired revert behavior.
  useEffect(() => {
    setMovePreview(null);
    setResizePreview(null);
  }, [object.bbox.x, object.bbox.y, object.bbox.w, object.bbox.h]);

  const baseBox: PxBox = {
    x: object.bbox.x * stageWidth,
    y: object.bbox.y * stageHeight,
    w: object.bbox.w * stageWidth,
    h: object.bbox.h * stageHeight,
  };
  const liveBox: PxBox = resizePreview ?? {
    x: movePreview?.x ?? baseBox.x,
    y: movePreview?.y ?? baseBox.y,
    w: baseBox.w,
    h: baseBox.h,
  };

  // Corner handles render on any selected box inside Stage 3 (i.e. when the
  // rect itself is draggable) — extracted and manual alike.
  const showAnchors = selected && draggable;

  function emitMoveBbox(x: number, y: number) {
    onDragEnd({
      x: clamp01(x / stageWidth),
      y: clamp01(y / stageHeight),
      w: object.bbox.w,
      h: object.bbox.h,
    });
  }

  function emitResizeBbox(box: PxBox) {
    onDragEnd({
      x: clamp01(box.x / stageWidth),
      y: clamp01(box.y / stageHeight),
      w: clamp01(box.w / stageWidth),
      h: clamp01(box.h / stageHeight),
    });
  }

  return (
    <>
      <Rect
        x={liveBox.x}
        y={liveBox.y}
        width={liveBox.w}
        height={liveBox.h}
        stroke={stroke}
        strokeWidth={thickness}
        dash={dashed ? [4, 4] : undefined}
        // Visible fill: when the operator opts into Mover caixa (Stage 3
        // move mode) OR the parent stage requested a tinted-all view (e.g.
        // Triagem). Alpha is baked into the fill color so the stroke stays
        // fully opaque.
        fill={tinted ? withAlpha(stroke, 0.18) : undefined}
        listening={interactive}
        // Hit-area depends on the stage and on move mode:
        // - drawMode (Stage 3) + NOT moveTarget: only the rect's perimeter
        //   (12 px band) is clickable, so clicks inside an existing rect's
        //   interior fall through to the stage's draw handler and start a
        //   new tag. Operators still grab the rect by its border to select.
        // - drawMode + moveTarget: the entire interior is draggable so the
        //   operator can grab the filled box anywhere to move it.
        // - Other stages (e.g. Stage 5 labelling): the entire interior is
        //   clickable so the operator can select a labeled tag from
        //   anywhere within its bbox.
        hitFunc={(context, shape) => {
          context.beginPath();
          context.rect(0, 0, shape.width(), shape.height());
          context.closePath();
          if (drawMode && !moveTarget) {
            const ctx = context as unknown as CanvasRenderingContext2D;
            const prevWidth = ctx.lineWidth;
            ctx.lineWidth = 12;
            context.strokeShape(shape);
            ctx.lineWidth = prevWidth;
          } else {
            context.fillShape(shape);
          }
        }}
        // Disable rect-level dragging while a corner is being dragged, so the
        // rect doesn't "follow" the corner translation.
        draggable={draggable && resizePreview === null}
        onMouseEnter={(e) => {
          if (!interactive) return;
          const stage = e.target.getStage();
          if (stage) stage.container().style.cursor = draggable ? "move" : "pointer";
        }}
        onMouseLeave={(e) => {
          const stage = e.target.getStage();
          if (stage) stage.container().style.cursor = "";
        }}
        dragBoundFunc={(pos) => ({
          x: Math.max(0, Math.min(stageWidth - liveBox.w, pos.x)),
          y: Math.max(0, Math.min(stageHeight - liveBox.h, pos.y)),
        })}
        onDragStart={(e) => {
          e.cancelBubble = true;
        }}
        onDragMove={(e) => {
          const node = e.target;
          setMovePreview({ x: node.x(), y: node.y() });
        }}
        onDragEnd={(e) => {
          const node = e.target;
          // Don't reset movePreview here — let the useEffect clear it when
          // the new bbox lands. Otherwise the rect snaps back during the
          // brief gap before the optimistic update arrives.
          emitMoveBbox(node.x(), node.y());
        }}
        // Selection runs on Konva's click event (mouseup with no significant
        // drag) — NOT on mousedown. Selecting on mousedown triggered a
        // parent re-render mid-event that disrupted Konva's drag start for
        // previously-unselected rects (the user click-drag in one motion).
        // Konva also handles cancelBubble for these, so the stage handler
        // doesn't fire on top of us.
        onClick={(e) => {
          if (!interactive) return;
          e.cancelBubble = true;
          onClick();
        }}
        onTap={(e) => {
          if (!interactive) return;
          e.cancelBubble = true;
          onClick();
        }}
        // Stop mousedown from bubbling so the stage's draw-mode handler
        // never fires when the user clicks on top of an existing rect.
        onMouseDown={(e) => {
          if (!interactive) return;
          e.cancelBubble = true;
        }}
        onTouchStart={(e) => {
          if (!interactive) return;
          e.cancelBubble = true;
        }}
      />
      {showAnchors && (
        <>
          <CornerHandle
            corner="nw"
            box={liveBox}
            stageWidth={stageWidth}
            stageHeight={stageHeight}
            onPreview={setResizePreview}
            onCommit={emitResizeBbox}
          />
          <CornerHandle
            corner="ne"
            box={liveBox}
            stageWidth={stageWidth}
            stageHeight={stageHeight}
            onPreview={setResizePreview}
            onCommit={emitResizeBbox}
          />
          <CornerHandle
            corner="sw"
            box={liveBox}
            stageWidth={stageWidth}
            stageHeight={stageHeight}
            onPreview={setResizePreview}
            onCommit={emitResizeBbox}
          />
          <CornerHandle
            corner="se"
            box={liveBox}
            stageWidth={stageWidth}
            stageHeight={stageHeight}
            onPreview={setResizePreview}
            onCommit={emitResizeBbox}
          />
        </>
      )}
      {object.class && <SkuLabel box={liveBox} code={object.class} />}
      {override?.badge && (
        <PairBadge
          box={liveBox}
          text={override.badge}
          color={stroke}
          corner={override.badgeCorner ?? "tl"}
        />
      )}
    </>
  );
}

/** Big A/B (etc.) marker drawn in the top-left corner of a box. Painted on
 *  top of the box stroke so the operator can tell the two overlap candidates
 *  apart at a glance. */
function PairBadge({
  box,
  text,
  color,
  corner,
}: {
  box: { x: number; y: number; w: number; h: number };
  text: string;
  color: string;
  corner: "tl" | "tr";
}) {
  // Estimate the rendered tag width so a top-right badge can hug the right
  // edge (Konva doesn't right-align Labels for us). Mono glyphs at fontSize
  // 18 are ~11px wide; padding adds 4px each side.
  const estWidth = text.length * 11 + 8;
  const x =
    corner === "tr"
      ? Math.max(box.x + 3, box.x + box.w - estWidth - 3)
      : box.x + 3;
  return (
    <Label x={x} y={box.y + 3} listening={false}>
      <Tag fill={color} cornerRadius={3} />
      <Text
        text={text}
        fill="#ffffff"
        fontFamily="var(--font-mono), monospace"
        fontSize={18}
        fontStyle="bold"
        padding={4}
      />
    </Label>
  );
}

const LABEL_HEIGHT = 18;

function SkuLabel({
  box,
  code,
}: {
  box: { x: number; y: number; w: number; h: number };
  code: string;
}) {
  // Place above the box. If it would clip past the page top, tuck inside.
  const aboveY = box.y - LABEL_HEIGHT;
  const y = aboveY < 0 ? box.y + 2 : aboveY;
  const tone = skuColor(code);
  return (
    <Label x={box.x} y={y} listening={false}>
      <Tag fill={tone.fill} cornerRadius={2} />
      <Text
        text={code}
        fill={tone.onColor}
        fontFamily="var(--font-mono), monospace"
        fontSize={11}
        padding={3}
      />
    </Label>
  );
}

type Corner = "nw" | "ne" | "sw" | "se";

function CornerHandle({
  corner,
  box,
  stageWidth,
  stageHeight,
  onPreview,
  onCommit,
}: {
  corner: Corner;
  box: { x: number; y: number; w: number; h: number };
  stageWidth: number;
  stageHeight: number;
  onPreview: (box: { x: number; y: number; w: number; h: number }) => void;
  onCommit: (box: { x: number; y: number; w: number; h: number }) => void;
}) {
  // Anchor position in stage pixels.
  const ax = corner === "nw" || corner === "sw" ? box.x : box.x + box.w;
  const ay = corner === "nw" || corner === "ne" ? box.y : box.y + box.h;

  // The opposite corner stays pinned during the drag; everything else moves.
  const opposite = {
    x: corner === "nw" || corner === "sw" ? box.x + box.w : box.x,
    y: corner === "nw" || corner === "ne" ? box.y + box.h : box.y,
  };

  function nextBox(px: number, py: number) {
    const x0 = Math.min(opposite.x, px);
    const y0 = Math.min(opposite.y, py);
    const x1 = Math.max(opposite.x, px);
    const y1 = Math.max(opposite.y, py);
    // Clamp to page; enforce minimum side length.
    const cx0 = Math.max(0, x0);
    const cy0 = Math.max(0, y0);
    const cx1 = Math.min(stageWidth, x1);
    const cy1 = Math.min(stageHeight, y1);
    const w = Math.max(MIN_RESIZE_PX, cx1 - cx0);
    const h = Math.max(MIN_RESIZE_PX, cy1 - cy0);
    return { x: cx0, y: cy0, w, h };
  }

  return (
    <Circle
      x={ax}
      y={ay}
      radius={6}
      fill={colors.panel}
      stroke={colors.manual}
      strokeWidth={2}
      draggable
      onMouseEnter={(e) => {
        const stage = e.target.getStage();
        if (stage) {
          // Cursor shapes per corner direction.
          stage.container().style.cursor =
            corner === "nw" || corner === "se" ? "nwse-resize" : "nesw-resize";
        }
      }}
      onMouseLeave={(e) => {
        const stage = e.target.getStage();
        if (stage) stage.container().style.cursor = "";
      }}
      onMouseDown={(e) => {
        e.cancelBubble = true;
      }}
      onTouchStart={(e) => {
        e.cancelBubble = true;
      }}
      onDragStart={(e) => {
        e.cancelBubble = true;
      }}
      onDragMove={(e) => {
        const node = e.target;
        onPreview(nextBox(node.x(), node.y()));
      }}
      onDragEnd={(e) => {
        const node = e.target;
        onCommit(nextBox(node.x(), node.y()));
      }}
    />
  );
}

const placeholderStyle: React.CSSProperties = {
  display: "grid",
  placeItems: "center",
  background: colors.canvas,
  border: `1px dashed ${colors.line}`,
  borderRadius: 4,
  fontSize: "var(--fs-ui)",
};
